import type { Vec2 } from '@sw/schema'
import type { SessionSocket } from './socket.js'

export interface BroadcasterInfo {
  participantId: string
  name: string
  position: Vec2 | null
}

export interface LiveAudioHandlers {
  onStream?: (participantId: string, stream: MediaStream) => void
  onStreamEnded?: (participantId: string) => void
  onBroadcasterChanged?: (broadcaster: BroadcasterInfo | undefined) => void
  onError?: (message: string) => void
}

/**
 * Public STUN only. Enough for most home and mobile networks; a relay would be
 * needed for the stubborn ones, and that is a service to pay for rather than
 * something to pretend works.
 */
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

/**
 * One live voice in a world.
 *
 * Connections are peer to peer, and listeners are the ones who dial: when the
 * world announces who is speaking, each listener offers to them directly. The
 * broadcaster never has to know who is out there, and the server only
 * introduces the two ends — it never carries a sample of audio, which is what
 * makes a live voice cost nothing to run.
 *
 * One speaker at a time. A second would need a forwarding server to mix and
 * fan out, and that is the next phase rather than a corner to cut here.
 */
export class LiveAudio {
  private readonly peers = new Map<string, RTCPeerConnection>()
  private localStream: MediaStream | undefined
  private broadcaster: BroadcasterInfo | undefined
  private broadcasting = false

  constructor(
    private readonly socket: SessionSocket,
    private readonly participantId: string,
    private readonly handlers: LiveAudioHandlers = {},
  ) {}

  get isBroadcasting(): boolean {
    return this.broadcasting
  }

  get currentBroadcaster(): BroadcasterInfo | undefined {
    return this.broadcaster
  }

  /** Called when the world says who is speaking, including on first connect. */
  handleBroadcaster(info: BroadcasterInfo | null): void {
    const previous = this.broadcaster
    this.broadcaster = info ?? undefined
    this.handlers.onBroadcasterChanged?.(this.broadcaster)

    if (previous && previous.participantId !== info?.participantId) {
      this.closePeer(previous.participantId)
      this.handlers.onStreamEnded?.(previous.participantId)
    }

    // Nothing to dial if there is no speaker, or if the speaker is us.
    if (!info || info.participantId === this.participantId) return
    if (this.peers.has(info.participantId)) return
    void this.dial(info.participantId)
  }

  /** A signalling message arrived for us. */
  async handleSignal(from: string, payload: unknown): Promise<void> {
    const message = payload as {
      sdp?: RTCSessionDescriptionInit
      candidate?: RTCIceCandidateInit
    }

    try {
      if (message.sdp?.type === 'offer') {
        // Somebody wants to hear us. Answer with the microphone attached.
        const peer = this.createPeer(from)
        for (const track of this.localStream?.getAudioTracks() ?? []) {
          peer.addTrack(track, this.localStream!)
        }
        await peer.setRemoteDescription(message.sdp)
        const answer = await peer.createAnswer()
        await peer.setLocalDescription(answer)
        this.send(from, { sdp: answer })
        return
      }

      const peer = this.peers.get(from)
      if (!peer) return

      if (message.sdp?.type === 'answer') {
        await peer.setRemoteDescription(message.sdp)
        return
      }
      if (message.candidate) await peer.addIceCandidate(message.candidate)
    } catch {
      this.handlers.onError?.('The live connection could not be set up.')
    }
  }

  /** Opens the microphone and tells the world. Starts muted: push to talk. */
  async startBroadcasting(position: Vec2 | null): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('This device has no microphone available.')
    }

    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
    this.setTalking(false)
    this.broadcasting = true
    this.socket.sendBroadcastStart(position)
  }

  /**
   * Push to talk. The track is disabled rather than the connection torn down,
   * so speaking again is instant and the peer connections stay warm.
   */
  setTalking(on: boolean): void {
    for (const track of this.localStream?.getAudioTracks() ?? []) track.enabled = on
  }

  moveTo(position: Vec2): void {
    if (this.broadcasting) this.socket.sendBroadcastPose(position)
  }

  stopBroadcasting(): void {
    this.broadcasting = false
    this.socket.sendBroadcastStop()
    for (const track of this.localStream?.getTracks() ?? []) track.stop()
    this.localStream = undefined
    for (const participantId of [...this.peers.keys()]) this.closePeer(participantId)
  }

  close(): void {
    if (this.broadcasting) this.stopBroadcasting()
    for (const participantId of [...this.peers.keys()]) this.closePeer(participantId)
  }

  private async dial(participantId: string): Promise<void> {
    try {
      const peer = this.createPeer(participantId)
      // Receive only: a listener has nothing to send.
      peer.addTransceiver('audio', { direction: 'recvonly' })
      const offer = await peer.createOffer()
      await peer.setLocalDescription(offer)
      this.send(participantId, { sdp: offer })
    } catch {
      this.handlers.onError?.('Could not connect to the live voice.')
    }
  }

  private createPeer(participantId: string): RTCPeerConnection {
    const existing = this.peers.get(participantId)
    if (existing) return existing

    const peer = new RTCPeerConnection({ iceServers: ICE_SERVERS })
    this.peers.set(participantId, peer)

    peer.addEventListener('icecandidate', (event) => {
      if (event.candidate) this.send(participantId, { candidate: event.candidate.toJSON() })
    })

    peer.addEventListener('track', (event) => {
      const [stream] = event.streams
      if (stream) this.handlers.onStream?.(participantId, stream)
    })

    peer.addEventListener('connectionstatechange', () => {
      if (['failed', 'closed', 'disconnected'].includes(peer.connectionState)) {
        this.closePeer(participantId)
        this.handlers.onStreamEnded?.(participantId)
      }
    })

    return peer
  }

  private closePeer(participantId: string): void {
    const peer = this.peers.get(participantId)
    if (!peer) return
    this.peers.delete(participantId)
    try {
      peer.close()
    } catch {
      // Closing something already closed is not worth reporting.
    }
  }

  private send(to: string, payload: unknown): void {
    this.socket.sendRtc(to, payload)
  }
}
